/**
 * Template Generator for Consumption Data Upload
 *
 * Generates downloadable template files in the expected formats:
 * 1. Standard Excel: Full-year format with hourly or 15-minute intervals
 * 2. CSV: Full-year format with 15-minute intervals
 *
 * Templates now generate a full year of data in a single file.
 * The backend parser reads timestamps directly from the file.
 */

import * as XLSX from 'xlsx';

/**
 * Generate a Standard Excel template for consumption data
 * Format: Full year with hourly data structure
 * Generates 8,760 rows (24 hours × 365 days)
 */
export function generateStandardExcelTemplate(): void {
  const now = new Date();
  const year = now.getFullYear();

  // Create header rows
  const headers = [
    ['Consumo Eléctrico - Plantilla (Hourly)'],
    ['Año:', `${year}`],
    ['Formato: Valores horarios en kWh'],
    ['Complete con sus datos de consumo. Puede llenar el año completo o solo los meses que necesite.'],
    [],
    ['Hora', 'Consumo (kWh)']
  ];

  // Create data rows for full year (24 hours × 365/366 days)
  const data: any[][] = [];
  const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

  for (let month = 1; month <= 12; month++) {
    const monthStr = month.toString().padStart(2, '0');
    const daysInMonth = new Date(year, month, 0).getDate();

    for (let day = 1; day <= daysInMonth; day++) {
      for (let hour = 0; hour < 24; hour++) {
        const hourStr = `${hour.toString().padStart(2, '0')}:00`;
        const dateStr = `${year}-${monthStr}-${day.toString().padStart(2, '0')} ${hourStr}`;
        data.push([dateStr, 0]); // Initialize with 0, user will fill in values
      }
    }
  }

  // Combine headers and data
  const worksheetData = [...headers, ...data];

  // Create workbook and worksheet
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(worksheetData);

  // Set column widths
  ws['!cols'] = [
    { wch: 25 }, // Hora column
    { wch: 15 }  // Consumo column
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Consumo');

  // Generate file and trigger download
  const fileName = `${year} Medida.xlsx`;
  XLSX.writeFile(wb, fileName);
}

/**
 * Generate a Standard Excel template with 15-minute intervals
 * Format: Full year with 15-minute interval structure
 * Generates 35,040 rows (96 intervals × 365 days) or 35,136 for leap years
 */
export function generateStandardExcel15MinTemplate(): void {
  const now = new Date();
  const year = now.getFullYear();

  // Create header rows
  const headers = [
    ['Consumo Eléctrico - Plantilla (15-min)'],
    ['Año:', `${year}`],
    ['Formato: Valores cada 15 minutos en kWh'],
    ['Complete con sus datos de consumo. Puede llenar el año completo o solo los meses que necesite.'],
    [],
    ['Medicion C/15 Min', 'Consumo (kWh)']
  ];

  // Create data rows for full year (96 intervals × 365/366 days)
  const data: any[][] = [];

  for (let month = 1; month <= 12; month++) {
    const monthStr = month.toString().padStart(2, '0');
    const daysInMonth = new Date(year, month, 0).getDate();

    for (let day = 1; day <= daysInMonth; day++) {
      for (let hour = 0; hour < 24; hour++) {
        for (let minute of [0, 15, 30, 45]) {
          const hourStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          const dateStr = `${year}-${monthStr}-${day.toString().padStart(2, '0')} ${hourStr}`;
          data.push([dateStr, 0]); // Initialize with 0
        }
      }
    }
  }

  // Combine headers and data
  const worksheetData = [...headers, ...data];

  // Create workbook and worksheet
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(worksheetData);

  // Set column widths
  ws['!cols'] = [
    { wch: 25 }, // Time column
    { wch: 15 }  // Consumo column
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Consumo');

  // Generate file and trigger download
  const fileName = `${year} Medida.xlsx`;
  XLSX.writeFile(wb, fileName);
}

/**
 * Generate a CSV template with 15-minute intervals
 * Format: Full year with fecha;Hora ;kwhd structure
 * Generates 35,040 rows (96 intervals × 365 days) or 35,136 for leap years
 */
export function generateGaribaldiCSVTemplate(): void {
  const now = new Date();
  const year = now.getFullYear();

  // Create CSV content
  let csvContent = 'fecha;Hora ;kwhd\n';

  for (let month = 1; month <= 12; month++) {
    const monthStr = month.toString().padStart(2, '0');
    const daysInMonth = new Date(year, month, 0).getDate();

    for (let day = 1; day <= daysInMonth; day++) {
      const dayStr = day.toString().padStart(2, '0');
      const dateStr = `${dayStr}-${monthStr}-${year}`; // DD-MM-YYYY format

      for (let hour = 0; hour < 24; hour++) {
        for (let minute of [0, 15, 30, 45]) {
          const timeStr = `${hour}:${minute.toString().padStart(2, '0')}`;
          csvContent += `${dateStr};${timeStr};0.0\n`; // Initialize with 0.0
        }
      }
    }
  }

  // Create blob and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${year}_template.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Generate a simple example CSV with sample data
 * This helps users understand what realistic consumption values look like
 * Generates one week of data with realistic patterns
 */
export function generateSampleDataCSV(): void {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const monthStr = month.toString().padStart(2, '0');

  // Create CSV content with sample data for one week
  let csvContent = 'fecha;Hora ;kwhd\n';

  for (let day = 1; day <= 7; day++) {
    const dayStr = day.toString().padStart(2, '0');
    const dateStr = `${dayStr}-${monthStr}-${year}`;

    for (let hour = 0; hour < 24; hour++) {
      for (let minute of [0, 15, 30, 45]) {
        const timeStr = `${hour}:${minute.toString().padStart(2, '0')}`;

        // Generate realistic sample consumption pattern
        // Lower at night (22:00-6:00), higher during day (8:00-18:00)
        let baseConsumption = 50; // kW base load
        if (hour >= 8 && hour < 18) {
          baseConsumption = 120; // Business hours
        } else if (hour >= 18 && hour < 22) {
          baseConsumption = 90; // Evening
        } else {
          baseConsumption = 40; // Night
        }

        // Add some randomness (±20%)
        const randomFactor = 0.8 + Math.random() * 0.4;
        const consumption = baseConsumption * randomFactor;

        // Convert to kWh for 15-minute interval (kW / 4)
        const kwh = (consumption / 4).toFixed(2);

        csvContent += `${dateStr};${timeStr};${kwh}\n`;
      }
    }
  }

  // Create blob and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${year}-${monthStr}_sample_data.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
